import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Container } from 'reactstrap';
import './ProjectsPlatformHe.css';

export default function ProjectsPlatformHe() {
  return (
    <div className="platform-page rtl-section">
      <Helmet>
        <title>ברוכים הבאים ל-EpicFinal | פלטפורמת פרויקטים</title>
        <meta name="description" content="הפלטפורמה שמנהלת עבורך את כל הפעילות הטכנולוגית. תכנון, פיתוח, תפעול ואבטחה במקום אחד." />
        <link rel="canonical" href="https://epicfinal.com/he/platform" />
      </Helmet>

      <section className="platform-hero">
        <Container>
          <motion.div
            className="platform-hero-inner"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="hero-content-wrapper">
              <div className="platform-badge-modern">
                <span className="badge-pulse"></span>
                <span>DevSecOps ONE</span>
              </div>
              <h1 className="hero-title"> תהליך התוכנה. במקום אחד</h1>
              <h2 className="hero-subtitle">מתכנון ופיתוח ועד תפעול ואבטחה — הכל מנוהל במקום אחד.</h2>

              <div className="hero-actions">
                <a
                  href="https://boarding.eu.epicfinal-cloud.com/login?lang=he"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary-action"
                >
                  <i className="las la-rocket"></i>
                  DevSecOps One
                </a>

                <a
                  href="https://my.epicfinal.com/index.php?language=hebrew&currency=2"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary-action"
                >
                  <i className="las la-cloud"></i>
                  תשתיות ענן
                </a>

                <a
                  href="https://epicfinal.com/he"
                  className="btn-outline-action"
                >
                  <i className="las la-info-circle"></i>
                  למידע נוסף
                </a>
              </div>

              {/* VIDEO SECTION */}
              <motion.div
                className="hero-video-wrapper"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <div className="video-responsive">
                  <iframe
                    src="https://www.youtube.com/embed/AsEa4XD4ZSA?rel=0&modestbranding=1"
                    title="DevSecOps Platform Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </motion.div>

            </div>

            <div className="hero-footer">
              <p className="footer-text">
                הירשם ל-DevSecOps One וקבל גישה לפרויקטים ובקשות ללא הגבלה.
              </p>
              <a
                href="https://epicfinal.com/pricing-he"
                target="_blank"
                rel="noopener noreferrer"
                className="pricing-link"
              >
                צפה במידע על המחירים
                <i className="las la-arrow-left"></i>
              </a>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section className="platform-how-it-works">
        <Container>
          <motion.div
            className="how-it-works-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>כך זה עובד</h2>
            <p>חמישה צעדים פשוטים מהרעיון ועד לביצוע</p>
          </motion.div>

          <div className="steps-container">
            {[
              { num: "1", icon: "🚀", title: "צור את הפרויקט שלך", desc: "התחל מיד" },
              { num: "2", icon: "🎯", title: "בחר את הדרך שלך", desc: "קבל ייעוץ מקצועי או התחל בהערכה החכמה שלנו" },
              { num: "3", icon: "📅", title: "קבע פגישה", desc: "נתאם יחד מטרות ודרישות" },
              { num: "4", icon: "📊", title: "עקוב אחר ההתקדמות", desc: "ראה כל שלב דרך הדשבורד שלך" },
              { num: "5", icon: "🎉", title: "הפרויקט הושלם", desc: "קבל קוד מקור או פרטי גישה — בקלות" },
            ].map((step, idx) => (
              <motion.div
                className="step-card"
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <div className="step-number">{step.num}</div>
                <div className="step-icon">{step.icon}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-desc">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* CLOSING SECTION */}
      <section className="platform-closing">
        <Container>
          <motion.div
            className="closing-inner"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="closing-title">
              פלטפורמה אחת. שליטה מלאה. <br />
              <span className="closing-highlight">בלי כאבי ראש.</span>
            </h2>
            <p className="closing-subtitle">מוכן לבנות חכם יותר?</p>

            <a
              href="https://boarding.eu.epicfinal-cloud.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-closing-action"
            >
              התחבר או הירשם כדי להתחיל
              <i className="las la-arrow-left"></i>
            </a>
          </motion.div>
        </Container>
      </section>
    </div>
  );
}
